import Uppy from '@uppy/core'
import German from '@uppy/locales/lib/de_DE.js'
import AwsS3 from '@uppy/aws-s3'
import Dashboard from '@uppy/dashboard'
import ApplicationController from '../../../../../javascript/controllers/application_controller'

// Connects to data-controller="views--publication--import--index-component--uploader"
export default class extends ApplicationController {
  static targets = ['fileInput', 'fileDescription', 'hiddenInput', 'form']

  static values = {
    maxFileSize: Number,
    locale: String,
    presignUrl: String,
  }

  connect() {
    super.connect()
    this.successfulUploads = []
    this.isProcessing = false

    // Build restrictions object conditionally - omit maxFileSize when unlimited (nil/0)
    const restrictions = {
      allowedFileTypes: ['application/pdf', '.pdf'],
    }
    if (this.maxFileSizeValue) {
      restrictions.maxFileSize = this.maxFileSizeValue
    }

    this.configs = {
      restrictions: restrictions,
    }

    this.uppy = this.initUppy()
    this.initUppyAws()
    this.initUppyUploadProgress()
    this.initUppyUploadSuccess()
  }

  disconnect() {
    this.uppy.destroy()
  }

  initUppy() {
    let uppy = new Uppy({
      autoProceed: true,
      locale: this.locale,
      restrictions: this.configs.restrictions,
    }).use(Dashboard, {
      target: this.fileInputTarget,
      singleFileFullScreen: false,
      inline: true,
      note: this.fileDescriptionTarget.innerText,
      width: 680,
      showProgressDetails: true,
      height: 430,
      proudlyDisplayPoweredByUppy: false,
    })

    return uppy
  }

  get locale() {
    return this.localeValue === 'de' ? German : null
  }

  resetUppy() {
    this.uppy.clear()
    this.formTarget.reset()
    this.successfulUploads = []
  }

  initUppyAws() {
    const presignUrl = this.presignUrlValue
    this.uppy.use(AwsS3, {
      shouldUseMultipart: () => false,
      getUploadParameters(file) {
        const filename = encodeURIComponent(file.meta.name)
        const type = encodeURIComponent(file.meta.type)
        return fetch(`${presignUrl}?filename=${filename}&type=${type}`).then((response) => {
          if (!response.ok) {
            throw new Error(`Server responded with ${response.status}`)
          }
          return response.json()
        })
      },
    })
  }

  initUppyUploadSuccess() {
    let self = this
    // The 'upload-success' event provides two parameters: file (UppyFile) and response (object)
    this.uppy.on('upload-success', (file, response) => {
      // Log success to AppSignal
      self.addBreadcrumb('upload', 'upload-success', {
        fileId: file.id,
        fileName: file.name,
        context: 'import-component',
      })

      if (response && response.body && response.body.key) {
        file.meta['key'] = response.body.key
      }
      self.successfulUploads.push(file)

      // Try to process files after each successful upload
      // processNextFile will handle queue management internally
      self.processNextFile()
    })
  }

  initUppyUploadProgress() {
    // The 'upload-error' event provides three parameters: file (UppyFile), error (Error) and response (object)
    this.uppy.on('upload-error', (file, error, response) => {
      // Add breadcrumb for upload error context
      this.addBreadcrumb('upload', 'upload-error', {
        fileId: file.id,
        fileName: file.name,
        fileType: file.type,
        isNetworkError: error.isNetworkError || false,
        responseStatus: response?.status,
        context: 'import-component',
        errorMessage: error.message,
      })

      this.reportError(error, {
        source: 'uppy',
        action: 'upload',
        fileName: file.name,
        responseStatus: response?.status,
        isNetworkError: error.isNetworkError || false,
      })
    })

    // Track file added events with breadcrumbs
    // The 'file-added' event provides one parameter: file (UppyFile)
    this.uppy.on('file-added', (file) => {
      this.addBreadcrumb('upload', 'file-added', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        context: 'import-component',
      })
    })

    // Track upload start events with breadcrumbs
    // The 'upload' event provides two parameters: uploadID (string) and files (UppyFile[])
    this.uppy.on('upload', (uploadID, files) => {
      this.addBreadcrumb('upload', 'upload-started', {
        uploadId: uploadID,
        fileCount: files.length,
        context: 'import-component',
      })
    })

    // Track completion events
    // The 'complete' event provides one parameter: result (object) with successful and failed arrays
    this.uppy.on('complete', (result) => {
      this.addBreadcrumb('upload', 'complete', {
        successfulCount: result && result.successful ? result.successful.length : 0,
        failedCount: result && result.failed ? result.failed.length : 0,
        context: 'import-component',
      })
    })

    // The 'error' event provides one parameter: error (Error)
    // Uppy errors must be manually reported because they don't bubble up
    // to the global error handler or Stimulus's installErrorHandler
    this.uppy.on('error', (error) => {
      this.reportError(error, { source: 'uppy', context: 'import-component' })
    })

    // The 'restriction-failed' event provides two parameters: file (UppyFile) and error (Error)
    this.uppy.on('restriction-failed', (file, error) => {
      // Expected user-facing validation — Uppy's Dashboard shows it. Breadcrumb only.
      this.addBreadcrumb('upload', 'restriction-failed', {
        fileName: file?.name,
        error: error.message,
        context: 'import-component',
      })
    })
  }

  uploadedFileData(file) {
    // construct uploaded file data in the format that Shrine expects
    // Check if key exists
    if (!file.meta || !file.meta['key']) {
      throw new Error('Could not find key in file metadata')
    }

    // Extract the ID from the key
    const match = file.meta['key'].match(/^cache\/(.+)/)
    if (!match) {
      throw new Error(`Invalid key format: ${file.meta['key']}`)
    }

    return JSON.stringify({
      id: match[1], // object key without prefix
      storage: 'cache',
      metadata: {
        size: file.size,
        filename: file.name,
        mime_type: file.type,
      },
    })
  }

  // Process the next file from the queue with an optional 'fromSubmit' parameter
  processNextFile(fromSubmit = false) {
    // If we're already processing and this isn't from a submit callback, exit
    if (this.isProcessing && !fromSubmit) return

    // If no files left, clean up and exit
    if (this.successfulUploads.length === 0) {
      this.isProcessing = false
      this.resetUppy() // Use the resetUppy method to clean up
      return
    }

    // Start/continue processing the next file
    this.isProcessing = true
    const uploadedFile = this.successfulUploads.shift()

    // Prepare and submit the form
    this.hiddenInputTarget.value = this.uploadedFileData(uploadedFile)

    // Remove the file from the dashboard after submission
    this.uppy.removeFile(uploadedFile.id)

    this.formTarget.requestSubmit()
  }

  submit() {
    // Continue processing files, explicitly marking this call as coming from submit
    this.processNextFile(true)
  }
}
